import { useStaticQueryWithPreview } from '@tedgustaf/gatsby-plugin-sanity-preview'
import { graphql, useStaticQuery } from 'gatsby'
import React, { PropsWithChildren, ReactNode, useMemo, useState } from 'react'
import {
  SanityHomePage,
  SanityHomePageConnection,
  SanitySearchResultsPage,
} from '../../types/generated/graphql-types'
import { usePageContext } from '../hooks/usePageContext'
import { sanityFooterToFooterProps } from '../mapping/FooterMapper'
import { mapSanityHeaderToHeaderProps } from '../mapping/HeaderMapper'
import { headerAndFooterPreviewQueryProjection } from '../preview/staticQueries/headerAndFooter'
import { CookieTractorTracking } from './CookieTractor/CookieTractorTracking'
import { SiteWideDisclaimer } from './Disclaimer/SiteWide/SiteWideDisclaimer'
import { Footer, FooterProps } from './Footer/Footer'
import { GtmNoScript } from './GoogleTagManager/GtmNoScript'
import { GtmScript } from './GoogleTagManager/GtmScript'
import { Header } from './Header/Header'
import { ParallaxWrapper } from './ParallaxWrapper'

export interface LayoutProps {
  currentPageUrl: string
  hideSignupForm?: boolean
  homePage?: SanityHomePage
  homePageLanguageVersions?: SanityHomePageConnection
  pageHasStickyMenu?: boolean
  searchResultsPage?: SanitySearchResultsPage
  useParallax?: boolean
}

export const Layout = ({
  children,
  currentPageUrl,
  hideSignupForm = false,
  homePage,
  homePageLanguageVersions,
  pageHasStickyMenu,
  searchResultsPage,
  useParallax,
}: PropsWithChildren<LayoutProps>) => {
  const data = useStaticQueryWithPreview({
    staticQueryData: useStaticQuery(graphql`
      query sanityFooterAndSanityHeader {
        ...HeaderFragment
        ...FooterFragment
      }
    `),
    query: headerAndFooterPreviewQueryProjection,
    options: useMemo(() => ({ addRawFields: true }), []),
  })

  const pageContext = usePageContext()

  const [fixedHeaderElements, setFixedHeaderElements] = useState<ReactNode>()

  const headers = data?.allSanityHeader?.nodes
  const footers = data?.allSanityFooter?.nodes

  const sanityHeaderProps =
    headers?.find(node => node.language === pageContext.language) ?? headers?.[0]

  const sanityFooterProps =
    footers?.find(node => node.language === pageContext.language) ?? footers?.[0]

  // Since the Header sets fixedHeaderElements which causes this component to redraw, we need to wrap the header
  // in a useMemo to keep it from ending up in a redraw loop.
  const header = useMemo(
    () => (
      <Header
        {...mapSanityHeaderToHeaderProps({
          currentPageUrl,
          header: sanityHeaderProps,
          homePageLanguageVersions,
          homePageUrl: homePage?.path ?? '',
          pageContext,
          searchResultsPage,
        })}
        setFixedElements={setFixedHeaderElements}
      />
    ),
    [
      currentPageUrl,
      homePage?.path,
      homePageLanguageVersions,
      pageContext,
      sanityHeaderProps,
      searchResultsPage,
    ]
  )

  const footerProps: FooterProps = sanityFooterProps
    ? sanityFooterToFooterProps(sanityFooterProps)
    : {}

  return (
    <>
      <CookieTractorTracking />
      <GtmScript />

      <GtmNoScript />

      <SiteWideDisclaimer>
        {fixedHeaderElements}

        {useParallax ? (
          <ParallaxWrapper pageHasStickyMenu={pageHasStickyMenu}>
            {header}
            {children}
            <Footer {...footerProps} hideSignupForm={hideSignupForm} />
          </ParallaxWrapper>
        ) : (
          <>
            {header}
            {children}
            <Footer {...footerProps} hideSignupForm={hideSignupForm} />
          </>
        )}
      </SiteWideDisclaimer>
    </>
  )
}
